import React, {
    forwardRef,
    useCallback,
    useState,
    useMemo,
    type RefObject,
} from 'react';
import styled from 'styled-components';

import { _ } from '@splunk/ui-utils/i18n';
import CaretSmallDown from '@splunk/react-icons/CaretSmallDown';
import CaretSmallRight from '@splunk/react-icons/CaretSmallRight';
import ScreenReaderContent from '@splunk/react-ui/ScreenReaderContent';
import { variables } from '@splunk/themes';

import type {
    DashboardApi,
    DashboardJSON,
    Mode,
} from '@splunk/dashboard-types';

import Title, { LINE_HEIGHT } from './header/Title';
import Description from './header/Description';
import { useTextInput } from './hooks/useTextInput';

const HeaderContainer = styled.div`
    padding: 8px 8px 0px 8px;
    background-color: ${variables.backgroundColorPage};
    box-sizing: border-box;
`;

const TitleContainer = styled.div`
    display: flex;
    align-items: flex-start;
    min-height: ${LINE_HEIGHT};
    box-sizing: inherit;
`;

const TriangleContainer = styled.button`
    display: flex;
    align-items: center;
    height: ${LINE_HEIGHT};
    box-sizing: inherit;
    color: ${variables.contentColorDefault};
    background: none;
    cursor: pointer;
    border: 0;
    padding: 0;
    margin-left: -8px;
`;

const CaretSmallDownStyled = styled(CaretSmallDown)`
    pointer-events: auto;
`;

const CaretSmallRightStyled = styled(CaretSmallRight)`
    pointer-events: auto;
`;

interface TriangleProps {
    isExpanded: boolean;
    onClick: () => void;
}

const Triangle = forwardRef<HTMLButtonElement, TriangleProps>(
    ({ isExpanded, onClick }, ref): JSX.Element => {
        return (
            <TriangleContainer ref={ref} onClick={onClick}>
                {isExpanded ? (
                    <>
                        <CaretSmallDownStyled data-test="triangle-header-expanded" />
                        <ScreenReaderContent>
                            {_('Show less')}
                        </ScreenReaderContent>
                    </>
                ) : (
                    <>
                        <CaretSmallRightStyled data-test="triangle-header-collapsed" />
                        <ScreenReaderContent>
                            {_('Show more')}
                        </ScreenReaderContent>
                    </>
                )}
            </TriangleContainer>
        );
    }
);

export interface HeaderProps {
    definition: DashboardJSON;
    mode: Mode;
    dashboardApi: DashboardApi;
    titleRef?: RefObject<HTMLDivElement | HTMLInputElement>;
    descRef?: RefObject<HTMLDivElement | HTMLInputElement>;
    handleRef?: RefObject<HTMLButtonElement>;
}

const DashboardHeader = forwardRef<HTMLDivElement, HeaderProps>(
    ({ definition, mode, dashboardApi, titleRef, descRef, handleRef }, ref) => {
        const [isExpanded, setIsExpanded] = useState(true);

        const handleDefinitionChange = useCallback(
            (def: DashboardJSON) => {
                dashboardApi?.updateDefinition(def);
            },
            [dashboardApi]
        );

        const handleExpandToggle = useCallback(() => {
            setIsExpanded((isExpandedVal) => !isExpandedVal);
        }, []);

        const handleTitleCommit = useCallback(
            (val?: string) =>
                handleDefinitionChange({
                    ...definition,
                    title: val,
                }),
            [handleDefinitionChange, definition]
        );

        const {
            onHandleBlur: onHandleBlurTitle,
            onHandleChange: onHandleChangeTitle,
            onHandleKeyDown: onHandleKeyDownTitle,
            value: valueTitle,
        } = useTextInput({
            initialValue: definition.title,
            handleCommit: handleTitleCommit,
            uniqKey: definition.title || '',
        });

        const handleDescCommit = useCallback(
            (val?: string) =>
                handleDefinitionChange({
                    ...definition,
                    description: val,
                }),
            [handleDefinitionChange, definition]
        );

        const {
            onHandleBlur: onHandleBlurDesc,
            onHandleChange: onHandleChangeDesc,
            onHandleKeyDown: onHandleKeyDownDesc,
            value: valueDesc,
        } = useTextInput({
            initialValue: definition.description,
            handleCommit: handleDescCommit,
            uniqKey: definition.description || '',
        });

        const renderTriangle = useMemo(() => {
            if (mode === 'edit') {
                return null;
            }
            return (
                <Triangle
                    ref={handleRef}
                    isExpanded={isExpanded}
                    onClick={handleExpandToggle}
                />
            );
        }, [handleRef, mode, isExpanded, handleExpandToggle]);

        if (!valueTitle && !valueDesc && mode === 'view') {
            // In this scenario, do not render this component at all as we want to maximize canvas space.
            return null;
        }

        return (
            <HeaderContainer data-test="dashboard-header" ref={ref}>
                {mode === 'view' && !valueTitle ? null : (
                    <TitleContainer data-test="dashboard-header-title">
                        <Title
                            ref={titleRef}
                            handleBlur={onHandleBlurTitle}
                            handleChange={onHandleChangeTitle}
                            handleKeyDown={onHandleKeyDownTitle}
                            isExpanded={isExpanded}
                            mode={mode}
                            value={valueTitle}
                        />
                        {mode === 'view' && valueDesc && renderTriangle}
                    </TitleContainer>
                )}
                {mode === 'view' && !valueDesc ? null : (
                    <Description
                        ref={descRef}
                        handleBlur={onHandleBlurDesc}
                        handleChange={onHandleChangeDesc}
                        handleKeyDown={onHandleKeyDownDesc}
                        showInViewMode={isExpanded}
                        mode={mode}
                        value={valueDesc}
                    />
                )}
            </HeaderContainer>
        );
    }
);

export default DashboardHeader;
